import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import api from '../api';

const GEWUENSCHT = [
  ['pirat', 'Pirat'], ['schwimmen_ab_10', 'Schwimmen lernen ab 10'], ['seepferdchen', 'Seepferdchen'],
  ['trixi', 'Trixi'], ['bronze', 'Bronze'], ['silber', 'Silber'], ['gold', 'Gold'],
];
const LETZTES = [
  ['keins', 'Keins'], ['seepferdchen', 'Seepferdchen'], ['trixi', 'Trixi'],
  ['bronze', 'Bronze'], ['silber', 'Silber'], ['gold', 'Gold'],
];
const STATUS = [
  ['neu', 'Neu'], ['kontaktiert', 'Kontaktiert'], ['platz_angeboten', 'Platzangebot'],
  ['angenommen', 'Angenommen'], ['abgelehnt', 'Abgelehnt'], ['storniert', 'Storniert'],
];
const LABEL = Object.fromEntries(GEWUENSCHT);

const heute = () => new Date().toLocaleDateString('sv-SE'); // YYYY-MM-DD
const datumFeld = (d) => (d ? String(d).slice(0, 10) : '');

const LEER = {
  kontakt_nachname: '', kontakt_vorname: '', kontakt_email: '', kontakt_telefon: '',
  schwimmer_vorname: '', schwimmer_nachname: '', schwimmer_geburtsdatum: '', schwimmer_geschlecht: '',
  gesundheitliche_hinweise: '', letztes_abzeichen: '', gewuenschtes_abzeichen: '',
  anmeldedatum: heute(), status: 'neu', bemerkung_intern: '',
};

function ausEintrag(e) {
  const f = { ...LEER };
  Object.keys(LEER).forEach(k => { if (e[k] != null) f[k] = String(e[k]); });
  f.schwimmer_geburtsdatum = datumFeld(e.schwimmer_geburtsdatum);
  f.anmeldedatum = datumFeld(e.anmeldedatum || e.created_at);
  return f;
}

const abschnitt = {
  fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--primary)', margin: '0.25rem 0 0.6rem', paddingBottom: 4, borderBottom: '2px solid var(--border)',
};
const raster = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 0.875rem' };

function Feld({ label, pflicht, fehler, hinweis, children }) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}{pflicht && <span style={{ color: 'var(--danger)' }}> *</span>}
      </label>
      {children}
      {hinweis && !fehler && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3 }}>{hinweis}</div>}
      {fehler && <div style={{ fontSize: '0.78rem', color: 'var(--danger)', marginTop: 3 }}>{fehler}</div>}
    </div>
  );
}

// Dialog zum Hinzufügen (ohne eintrag) oder Korrigieren (mit eintrag) eines Wartelisten-Eintrags.
// Nur für Admin / Schwimmadmin.
export default function WartelisteHinzufuegen({ eintrag = null, onClose, onGespeichert }) {
  const bearbeiten = !!eintrag;
  const start = useMemo(() => (eintrag ? ausEintrag(eintrag) : LEER), [eintrag]);
  const startZusatz = useMemo(() => ({ ...(eintrag?.zusatzfelder || {}) }), [eintrag]);

  const [form, setForm] = useState(start);
  const [zusatz, setZusatz] = useState(startZusatz);
  const [zusatzFelder, setZusatzFelder] = useState([]);
  const [einwilligung, setEinwilligung] = useState(false);
  const [neuEinsortieren, setNeuEinsortieren] = useState(true);
  const [fehler, setFehler] = useState({});
  const [meldung, setMeldung] = useState('');
  const [duplikat, setDuplikat] = useState('');
  const [speichern, setSpeichern] = useState(false);

  // Zusatzfelder der Warteliste-Kachel; beim Bearbeiten auch vorhandene, nicht mehr konfigurierte Antworten
  useEffect(() => {
    api.get('/api/admin/kacheln').then(r => {
      const k = r.data.find(x => x.key === 'warteliste');
      const konfiguriert = (k?.formular_felder || []).filter(f => !f.system && f.typ !== 'ueberschrift');
      const labels = new Set(konfiguriert.map(f => f.label));
      const uebrige = Object.keys(startZusatz).filter(l => !labels.has(l))
        .map(l => ({ id: `alt_${l}`, label: l, typ: typeof startZusatz[l] === 'boolean' ? 'boolean' : 'text', hinweis: 'Feld ist nicht mehr im Formular' }));
      setZusatzFelder([...konfiguriert, ...uebrige]);
    }).catch(() => {});
  }, [startZusatz]);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (fehler[k]) setFehler(p => ({ ...p, [k]: '' }));
    setDuplikat('');
  };

  const geaendert = JSON.stringify(form) !== JSON.stringify(start) || JSON.stringify(zusatz) !== JSON.stringify(startZusatz);

  // Versehentliches Schließen (Klick neben den Dialog, Esc) nicht ohne Rückfrage
  const schliessen = useCallback(() => {
    if (geaendert && !window.confirm('Änderungen verwerfen?')) return;
    onClose();
  }, [geaendert, onClose]);

  const pruefen = () => {
    const f = {};
    const p = (k, name) => { if (!String(form[k]).trim()) f[k] = `${name} ist Pflicht`; };
    p('kontakt_nachname', 'Nachname'); p('kontakt_vorname', 'Vorname');
    p('kontakt_email', 'E-Mail'); p('kontakt_telefon', 'Telefon');
    p('schwimmer_vorname', 'Vorname'); p('schwimmer_nachname', 'Nachname');
    p('schwimmer_geburtsdatum', 'Geburtsdatum');
    if (form.kontakt_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.kontakt_email.trim())) f.kontakt_email = 'Keine gültige E-Mail-Adresse';
    if (form.schwimmer_geburtsdatum && form.schwimmer_geburtsdatum > heute()) f.schwimmer_geburtsdatum = 'Liegt in der Zukunft';
    if (!form.schwimmer_geschlecht) f.schwimmer_geschlecht = 'Bitte wählen';
    if (!form.gewuenschtes_abzeichen) f.gewuenschtes_abzeichen = 'Bitte wählen';
    if (!form.anmeldedatum) f.anmeldedatum = 'Anmeldedatum ist Pflicht';
    else if (form.anmeldedatum > heute()) f.anmeldedatum = 'Liegt in der Zukunft';
    else if (form.schwimmer_geburtsdatum && form.anmeldedatum < form.schwimmer_geburtsdatum) f.anmeldedatum = 'Liegt vor dem Geburtsdatum';
    if (!bearbeiten && !einwilligung) f.einwilligung = 'Ohne Einwilligung darf der Eintrag nicht angelegt werden';
    return f;
  };

  const handleSpeichern = async (trotzdem = false) => {
    const f = pruefen();
    setFehler(f);
    setMeldung('');
    if (Object.keys(f).length) { setMeldung('Bitte die markierten Felder prüfen.'); return; }
    if (bearbeiten && !geaendert) { onClose(); return; }

    setSpeichern(true);
    try {
      const r = bearbeiten
        ? await api.put(`/api/admin/warteliste/${eintrag.id}/daten`, {
            ...form, zusatzfelder: zusatz, neu_einsortieren: neuEinsortieren, trotzdem_speichern: trotzdem,
          })
        : await api.post('/api/admin/warteliste', {
            ...form, zusatzfelder: zusatz, einwilligung: true, trotzdem_anlegen: trotzdem,
          });
      onGespeichert(r.data);
    } catch (err) {
      const d = err.response?.data || {};
      if (err.response?.status === 409 && d.duplikat) {
        setDuplikat(d.error);
      } else {
        if (d.felder) setFehler(d.felder);
        setMeldung(d.error || 'Fehler beim Speichern');
      }
    } finally {
      setSpeichern(false);
    }
  };

  const input = (k, type = 'text', extra = {}) => (
    <input type={type} className="form-control" value={form[k]} onChange={e => set(k, e.target.value)}
      style={fehler[k] ? { borderColor: 'var(--danger)' } : undefined} {...extra} />
  );
  const auswahl = (k, optionen, leer = '– Bitte wählen –') => (
    <select className="form-control" value={form[k]} onChange={e => set(k, e.target.value)}
      style={fehler[k] ? { borderColor: 'var(--danger)' } : undefined}>
      {leer !== null && <option value="">{leer}</option>}
      {optionen.map(([w, l]) => <option key={w} value={w}>{l}</option>)}
    </select>
  );

  // Hinweise zur Position in der Wartelisten-Kategorie
  const kategorieGeaendert = bearbeiten && form.gewuenschtes_abzeichen && form.gewuenschtes_abzeichen !== start.gewuenschtes_abzeichen;
  const datumGeaendert = bearbeiten && form.anmeldedatum && form.anmeldedatum !== start.anmeldedatum;
  let datumHinweis;
  if (!bearbeiten) {
    datumHinweis = form.anmeldedatum && form.anmeldedatum < heute()
      ? 'Rückdatiert: Das Kind wird in seiner Ausbildung nach diesem Datum einsortiert, spätere Anmeldungen rücken nach.'
      : 'Kann rückdatiert werden, z.B. bei Papier-Anmeldungen.';
  }

  const geschlechtOptionen = [['maennlich', 'Männlich'], ['weiblich', 'Weiblich'], ['divers', 'Divers']];
  if (bearbeiten && start.schwimmer_geschlecht === 'unbekannt') geschlechtOptionen.push(['unbekannt', 'Unbekannt (bitte korrigieren)']);

  return (
    <Modal
      title={bearbeiten
        ? `Warteliste #${eintrag.id} – Daten bearbeiten`
        : 'Warteliste – Kind hinzufügen'}
      onClose={schliessen} size="lg">

      <div style={abschnitt}>Kontaktperson (Elternteil)</div>
      <div style={raster}>
        <Feld label="Nachname" pflicht fehler={fehler.kontakt_nachname}>{input('kontakt_nachname', 'text', { maxLength: 100, autoFocus: true })}</Feld>
        <Feld label="Vorname" pflicht fehler={fehler.kontakt_vorname}>{input('kontakt_vorname', 'text', { maxLength: 100 })}</Feld>
        <Feld label="E-Mail" pflicht fehler={fehler.kontakt_email}>{input('kontakt_email', 'email', { maxLength: 255 })}</Feld>
        <Feld label="Telefon" pflicht fehler={fehler.kontakt_telefon}>{input('kontakt_telefon', 'tel', { maxLength: 50 })}</Feld>
      </div>

      <div style={abschnitt}>Kind</div>
      <div style={raster}>
        <Feld label="Vorname" pflicht fehler={fehler.schwimmer_vorname}>{input('schwimmer_vorname', 'text', { maxLength: 100 })}</Feld>
        <Feld label="Nachname" pflicht fehler={fehler.schwimmer_nachname}>{input('schwimmer_nachname', 'text', { maxLength: 100 })}</Feld>
        <Feld label="Geburtsdatum" pflicht fehler={fehler.schwimmer_geburtsdatum}>{input('schwimmer_geburtsdatum', 'date', { max: heute() })}</Feld>
        <Feld label="Geschlecht" pflicht fehler={fehler.schwimmer_geschlecht}>
          {auswahl('schwimmer_geschlecht', geschlechtOptionen)}
        </Feld>
      </div>

      <div style={abschnitt}>Schwimmdetails</div>
      <div style={raster}>
        <Feld label="Gewünschte Ausbildung" pflicht fehler={fehler.gewuenschtes_abzeichen}
          hinweis={kategorieGeaendert
            ? `Wechsel von „${LABEL[start.gewuenschtes_abzeichen] || start.gewuenschtes_abzeichen}" zu „${LABEL[form.gewuenschtes_abzeichen]}": Das Kind wird dort nach seinem Anmeldedatum einsortiert.`
            : undefined}>
          {auswahl('gewuenschtes_abzeichen', GEWUENSCHT)}
        </Feld>
        <Feld label="Letztes abgeschlossenes Abzeichen" fehler={fehler.letztes_abzeichen}>{auswahl('letztes_abzeichen', LETZTES, '– nicht angegeben –')}</Feld>
      </div>
      <Feld label="Gesundheitliche Hinweise" fehler={fehler.gesundheitliche_hinweise}>
        <textarea className="form-control" rows={2} value={form.gesundheitliche_hinweise}
          onChange={e => set('gesundheitliche_hinweise', e.target.value)}
          placeholder="Allergien, Einschränkungen, besondere Bedürfnisse…" />
      </Feld>

      {zusatzFelder.length > 0 && (<>
        <div style={abschnitt}>Weitere Angaben</div>
        <div style={raster}>
          {zusatzFelder.map(f => (
            <Feld key={f.id} label={f.label} hinweis={f.hinweis}>
              {f.typ === 'boolean' ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.9rem', height: 36 }}>
                  <input type="checkbox" checked={!!zusatz[f.label]} style={{ width: 16, height: 16 }}
                    onChange={e => setZusatz(p => ({ ...p, [f.label]: e.target.checked }))} /> Ja
                </label>
              ) : f.typ === 'select' ? (
                <select className="form-control" value={zusatz[f.label] ?? ''}
                  onChange={e => setZusatz(p => ({ ...p, [f.label]: e.target.value }))}>
                  <option value="">– nicht angegeben –</option>
                  {(f.optionen || []).map(o => <option key={o} value={o}>{o}</option>)}
                  {zusatz[f.label] && !(f.optionen || []).includes(zusatz[f.label]) && (
                    <option value={zusatz[f.label]}>{zusatz[f.label]} (bisheriger Wert)</option>
                  )}
                </select>
              ) : f.typ === 'textarea' ? (
                <textarea className="form-control" rows={2} value={zusatz[f.label] ?? ''}
                  onChange={e => setZusatz(p => ({ ...p, [f.label]: e.target.value }))} />
              ) : (
                <input className="form-control" value={zusatz[f.label] ?? ''}
                  type={['date', 'number', 'email'].includes(f.typ) ? f.typ : 'text'}
                  onChange={e => setZusatz(p => ({ ...p, [f.label]: e.target.value }))} />
              )}
            </Feld>
          ))}
        </div>
      </>)}

      <div style={abschnitt}>Verwaltung</div>
      <div style={raster}>
        <Feld label="Anmeldedatum" pflicht fehler={fehler.anmeldedatum} hinweis={datumHinweis}>
          {input('anmeldedatum', 'date', { max: heute() })}
        </Feld>
        {!bearbeiten && <Feld label="Status" fehler={fehler.status}>{auswahl('status', STATUS, null)}</Feld>}
      </div>
      {datumGeaendert && !kategorieGeaendert && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.85rem', margin: '-0.4rem 0 1rem' }}>
          <input type="checkbox" checked={neuEinsortieren} onChange={e => setNeuEinsortieren(e.target.checked)}
            style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }} />
          <span>
            Position in der Warteliste nach dem neuen Anmeldedatum bestimmen
            <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.78rem' }}>
              Ohne Häkchen bleibt die bisherige Position (Nr. {eintrag.reihenfolge ?? '–'}) erhalten.
            </span>
          </span>
        </label>
      )}
      <Feld label="Interne Bemerkung">
        <textarea className="form-control" rows={2} value={form.bemerkung_intern}
          onChange={e => set('bemerkung_intern', e.target.value)} placeholder={'Nur für Admins sichtbar, z.B. „telefonisch angemeldet“'} />
      </Feld>

      {bearbeiten ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Der Status wird wie gewohnt im Detailfenster geändert. Die Korrektur wird im Protokoll festgehalten.
        </div>
      ) : (
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.88rem', marginBottom: '1rem',
          background: fehler.einwilligung ? '#ffebee' : '#f4f7fb', border: `1px solid ${fehler.einwilligung ? 'var(--danger)' : 'var(--border)'}`,
          borderRadius: 6, padding: '0.6rem 0.75rem',
        }}>
          <input type="checkbox" checked={einwilligung} style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
            onChange={e => { setEinwilligung(e.target.checked); setFehler(p => ({ ...p, einwilligung: '' })); }} />
          <span>
            Die Kontaktperson wurde über die Datenschutzerklärung informiert und ist mit der Speicherung der Daten
            (inkl. gesundheitlicher Hinweise) einverstanden. <span style={{ color: 'var(--danger)' }}>*</span>
            {fehler.einwilligung && <span style={{ display: 'block', color: 'var(--danger)', fontSize: '0.78rem' }}>{fehler.einwilligung}</span>}
          </span>
        </label>
      )}

      {meldung && <div className="alert alert-error">{meldung}</div>}

      {duplikat && (
        <div className="alert" style={{ background: '#fff8e1', border: '1px solid #f9a825', color: '#5d4037' }}>
          <strong>⚠ Mögliche Doppelanmeldung:</strong> {duplikat}
          <div style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => handleSpeichern(true)} disabled={speichern}>
              {bearbeiten ? 'Trotzdem speichern' : 'Trotzdem anlegen'}
            </button>
          </div>
        </div>
      )}

      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={schliessen} disabled={speichern}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" onClick={() => handleSpeichern(false)} disabled={speichern}
          style={{ marginLeft: 'auto' }}>
          {speichern ? 'Speichern…' : bearbeiten ? '✓ Änderungen speichern' : '✓ Auf die Warteliste setzen'}
        </button>
      </div>
    </Modal>
  );
}
